import { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getMethodByYear, updateMethod } from "../../../api/settings";

const PreferenceSetting = () => {
  const { selectedYear } = useAcademicYear();

  //Initialisation des state utilisés
  const createSchema = z
    .object({
      signatory1: z.string().nullable(),
      signatory2: z.string().nullable(),
      signatoryAttestation1: z.string().nullable(),
      signatoryAttestation2: z.string().nullable(),
      postSignatory1: z.string().nullable(),
      postSignatory2: z.string().nullable(),
      postSignatoryAttestation1: z.string().nullable(),
      postSignatoryAttestation2: z.string().nullable(),
      compensation: z.boolean({
        required_error: "La valeur de la compensation est requise",
        invalid_type_error:
          "La valeur de la compensation doit être vraie ou fausse",
      }),
      weightNoteClass: z
        .number({
          invalid_type_error: "Le poids de la note de classe est requis",
        })
        .min(1, "Le poids doit être au minimum 1%")
        .max(100, "Le poids doit être au maximum 100%"),
      weightNoteExamen: z
        .number({
          invalid_type_error: "Le poids de la note d'examen est requis",
        })
        .min(1, "Le poids doit être au minimum 1%")
        .max(100, "Le poids doit être au maximum 100%"),
      displayCoefficient: z.boolean({
        required_error: "La valeur du coefficient est requise",
        invalid_type_error:
          "La valeur du coefficient doit être vraie ou fausse",
      }),
      typeNoteAllowed: z.enum(["Note Classe", "Note Examen"], {
        required_error:
          "Le type de note autorisé pour les professeurs est requis",
        invalid_type_error:
          "Le type de note autorisé doit être 'Note Classe' ou 'Note Examen'",
      }),
    })
    .refine((data) => data.weightNoteClass + data.weightNoteExamen === 100, {
      message:
        "La somme des poids de classe et d'examen doit être égale à 100%",
      path: ["weightNoteClass"], // Ou ["weightNoteClass"], choisis où afficher l'erreur
    });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const finalData = {
        ...data,
      };
      // console.log(finalData);
      //Envoi des datas pour la MÀJ vers le backend
      await updateMethod(finalData, selectedYear?.id);

      toast.success("Paramètres de préference mis à jour !"); //Toast de succès
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  //On recupère la liste des Years en appelant sa fonction
  useEffect(() => {
    if (!selectedYear) return; // ✅ Ne rien faire si l'année académique n'es pas définie

    const fetchMethodCalcul = async () => {
      try {
        const response = await getMethodByYear(selectedYear?.id);
        if (!response) {
          // Si aucune année trouvé
          throw new Error("Méthode de calcul introuvable");
        }
        // console.log(response.data)
        reset({
          // Si année trouvé on rempli les champs du formulaire
          signatory1: response.data[0].signatory1,
          signatory2: response.data[0].signatory2,
          signatoryAttestation1: response.data[0].signatoryAttestation1,
          signatoryAttestation2: response.data[0].signatoryAttestation2,
          postSignatory1: response.data[0].postSignatory1,
          postSignatory2: response.data[0].postSignatory2,
          postSignatoryAttestation1: response.data[0].postSignatoryAttestation1,
          postSignatoryAttestation2: response.data[0].postSignatoryAttestation2,
          compensation: response.data[0].compensation,
          weightNoteClass: response.data[0].weightNoteClass,
          weightNoteExamen: response.data[0].weightNoteExamen,
          displayCoefficient: response.data[0].displayCoefficient,
          typeNoteAllowed: response.data[0].typeNoteAllowed,
        });
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
      }
    };

    fetchMethodCalcul();
  }, [selectedYear]);

  return (
    <>
      <form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex align-items-center justify-content-between flex-wrap border-bottom pt-3 mb-3">
          <div className="mb-3">
            <h5 className="mb-1">Préferences</h5>
            <p>
              Personnalisez le parcours et les paramètres de votre application
            </p>
          </div>
          <div className="mb-3">
            <button className="btn btn-primary" type="submit">
              Enregistrer
            </button>
          </div>
        </div>
        <div className="d-md-flex d-block">
          <div className="flex-fill">
            <div className="card">
              <div className="card-header">
                <h5>Calcul des Notes</h5>
              </div>
              <div className="card-body p-3 pb-0">
                <div className="row">
                  <div className="col-xxl-6 col-xl-6 col-sm-6">
                    <div className="d-md-flex justify-content-between align-items-center border rounded bg-white p-3 mb-3">
                      <h5 className="fw-normal fs-15">
                        Compensation des notes
                      </h5>
                      <div className="status-toggle modal-status">
                        <input
                          {...register("compensation")}
                          type="checkbox"
                          id="user1"
                          className="check"
                        />
                        <label htmlFor="user1" className="checktoggle">
                          {" "}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-xxl-6 col-xl-6 col-sm-6">
                    <div className="d-md-flex justify-content-between align-items-center border rounded bg-white p-3 mb-3">
                      <h5 className="fw-normal fs-15">Affichage Coefficient</h5>
                      <div className="status-toggle modal-status">
                        <input
                          {...register("displayCoefficient")}
                          type="checkbox"
                          id="user2"
                          className="check"
                        />
                        <label htmlFor="user2" className="checktoggle">
                          {" "}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-xxl-4 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Note de Classe (%)</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("weightNoteClass", {
                              valueAsNumber: true,
                            })}
                            type="number"
                            className={
                              errors.weightNoteClass
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: 18%"
                            aria-label="note_classe"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            %
                          </span>
                          {errors.weightNoteClass && (
                            <div className="invalid-feedback">
                              {errors.weightNoteClass.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-4 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Note d'Examen (%)</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("weightNoteExamen", {
                              valueAsNumber: true,
                            })}
                            type="number"
                            className={
                              errors.weightNoteExamen
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: 48%"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            %
                          </span>
                          {errors.weightNoteExamen && (
                            <div className="invalid-feedback">
                              {errors.weightNoteExamen.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-4 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Note de Prof (%)</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <select
                            {...register("typeNoteAllowed")}
                            className={
                              errors.typeNoteAllowed
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            aria-label="note_classe"
                            aria-describedby="basic-addon1"
                          >
                            <option value="Note Classe">Note de Classe</option>
                            <option value="Note Examen">Note d'Examen</option>
                          </select>

                          <span className="input-group-text" id="basic-addon1">
                            %
                          </span>
                          {errors.typeNoteAllowed && (
                            <div className="invalid-feedback">
                              {errors.typeNoteAllowed.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h5>Signataires Bulletin de Notes</h5>
              </div>
              <div className="card-body p-3 pb-0">
                <div className="row">
                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Nom Signataire 1</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("signatory1")}
                            type="text"
                            className={
                              errors.signatory1
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Dr Boubacar Sanogo"
                            aria-label="note_classe"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.signatory1 && (
                            <div className="invalid-feedback">
                              {errors.signatory1.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        Fonction Signataire 1
                      </label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("postSignatory1")}
                            type="text"
                            className={
                              errors.postSignatory1
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Recteur"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-briefcase" />
                          </span>
                          {errors.postSignatory1 && (
                            <div className="invalid-feedback">
                              {errors.postSignatory1.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Nom Signataire 2</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("signatory2")}
                            type="text"
                            className={
                              errors.signatory2
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Diallo Oumar"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.signatory2 && (
                            <div className="invalid-feedback">
                              {errors.signatory2.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        Fonction Signataire 2
                      </label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("postSignatory2")}
                            type="text"
                            className={
                              errors.postSignatory2
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Vice Recteur"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-briefcase" />
                          </span>
                          {errors.postSignatory2 && (
                            <div className="invalid-feedback">
                              {errors.postSignatory2.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h5>Signataires Attestation</h5>
              </div>
              <div className="card-body p-3 pb-0">
                <div className="row">
                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Nom Signataire 1</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("signatoryAttestation1")}
                            type="text"
                            className={
                              errors.signatoryAttestation1
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Dr Boubacar Sanogo"
                            aria-label="note_classe"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.signatoryAttestation1 && (
                            <div className="invalid-feedback">
                              {errors.signatoryAttestation1.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        Fonction Signataire 1
                      </label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("postSignatoryAttestation1")}
                            type="text"
                            className={
                              errors.postSignatoryAttestation1
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Recteur"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-briefcase" />
                          </span>
                          {errors.postSignatoryAttestation1 && (
                            <div className="invalid-feedback">
                              {errors.postSignatoryAttestation1.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Nom Signataire 2</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("signatoryAttestation2")}
                            type="text"
                            className={
                              errors.signatoryAttestation2
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Diallo Oumar"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.signatoryAttestation2 && (
                            <div className="invalid-feedback">
                              {errors.signatoryAttestation2.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        Fonction Signataire 2
                      </label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("postSignatoryAttestation2")}
                            type="text"
                            className={
                              errors.postSignatoryAttestation2
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Vice Recteur"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-briefcase" />
                          </span>
                          {errors.postSignatoryAttestation2 && (
                            <div className="invalid-feedback">
                              {errors.postSignatoryAttestation2.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default PreferenceSetting;
